import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateTrackQueries } from "./invalidateTrackQueries";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { getLeadTracks } from "@/config/endpoints";
import { invalidateLeadTimeline } from "@/lib/queryClient";
import type { EntityType } from "../types/stageTypes";
import {
  readTrackRequirementRefusal,
  requestTrackRequirementFix,
} from "@/lib/trackRequirementGate";

/**
 * TRACK REQUIREMENT GATE. A change the server refuses because the lead does
 * not meet a track requirement opens the "needs a few things first" popup
 * (TrackRequirementHost), where the person adds what is missing; "continue"
 * replays the very same request. Cancelling (or no mounted host) throws the
 * original error, so the caller's own toast still applies. Bounded so a
 * gate that can never be met cannot loop forever.
 */
async function withTrackRequirementPrompt<T>(
  run: () => Promise<T>,
  ctx: { entityType: EntityType; leadId: number; childId: number | null },
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      return await run();
    } catch (error: any) {
      lastError = error;
      const refusal = readTrackRequirementRefusal(error);
      if (!refusal) throw error;
      const again = await requestTrackRequirementFix({
        message: refusal.message,
        failures: refusal.failures,
        trackId: refusal.trackId,
        subTrackId: refusal.subTrackId,
        entityType: refusal.entityType ?? (ctx.entityType === "child" ? "child" : "lead"),
        leadId: refusal.leadId ?? ctx.leadId,
        childId: refusal.childId ?? ctx.childId,
      });
      if (!again) throw error;
    }
  }
  throw lastError;
}

/**
 * A refused track change. The server answers a track requirement (Stage &
 * Status → Tracks → Requirements) with the stage validator's shape:
 * `message` = the first gate's message, `requirements` = every gate that
 * failed. Show them all, so the person fixes the lead once, not per retry.
 */
function toastTrackError(error: any, fallback: string) {
  const body = error?.response?.data;
  const msg: string = body?.message || fallback;
  // The error handler puts an AppError's extras under `data` — the same
  // place the stage gate's `noteRequired` lives (transitionNoteGate.ts).
  const extras = body?.data ?? body;
  const failures: { referenceKey?: string; errorMessage?: string }[] = Array.isArray(
    extras?.requirements,
  )
    ? extras.requirements
    : [];
  if (failures.length > 1) {
    const lines = failures
      .map((f) => f.errorMessage || f.referenceKey)
      .filter(Boolean)
      .filter((line, i, arr) => arr.indexOf(line) === i);
    toast.error(msg, { description: lines.join(" · ") });
    return;
  }
  toast.error(msg);
}

interface TrackMutationBody {
  trackId: number;
  subTrackId: number;
  /** Track person picked in the "who will handle this?" popup. "auto" = next
   *  by turn. Omitted = legacy call, the server does nothing extra. */
  userId?: number | "auto";
  /** The deal, on a deal-based track only (see `trackWriteDealId`). */
  childId?: number;
}

function invalidateEntityTracks(
  queryClient: ReturnType<typeof useQueryClient>,
  entityType: EntityType,
  entityId: string | number
) {
  queryClient.invalidateQueries({
    queryKey: ["entity-tracks", entityType, entityId],
  });
  // Any track change can open or close a track follow-up, and moves the
  // dashboard's track cards.
  invalidateTrackQueries(queryClient);
}

export function useEntityTrackMutations(
  entityType: EntityType,
  entityId: string | number | null | undefined,
  leadId: string | number | null | undefined
) {
  const queryClient = useQueryClient();

  const gateCtx = (body: TrackMutationBody) => ({
    entityType,
    leadId: Number(leadId),
    childId: body.childId != null ? Number(body.childId) : null,
  });

  const assignTrack = useMutation({
    mutationFn: async (body: TrackMutationBody) =>
      withTrackRequirementPrompt(async () => {
        const { data } = await axiosInstance.post(getLeadTracks, {
          ...body,
          leadId,
        });
        return data;
      }, gateCtx(body)),
    onSuccess: () => {
      toast.success("Track assigned successfully");
      if (entityId) invalidateEntityTracks(queryClient, entityType, entityId);
      invalidateLeadTimeline(leadId);
    },
    onError: (error: any) => {
      toastTrackError(error, "Failed to assign track");
    },
  });

  const switchSubTrack = useMutation({
    mutationFn: async (body: TrackMutationBody) =>
      withTrackRequirementPrompt(async () => {
        const { data } = await axiosInstance.patch(getLeadTracks, {
          ...body,
          leadId,
        });
        return data;
      }, gateCtx(body)),
    onSuccess: () => {
      toast.success("Sub-track updated successfully");
      if (entityId) invalidateEntityTracks(queryClient, entityType, entityId);
      invalidateLeadTimeline(leadId);
    },
    onError: (error: any) => {
      toastTrackError(error, "Failed to switch sub-track");
    },
  });

  /** Hand an already-assigned track to a person (or "auto" = next by turn). */
  const assignTrackUser = useMutation({
    mutationFn: async (body: {
      trackId: number;
      userId: number | "auto";
      childId?: number;
    }) => {
      const { data } = await axiosInstance.post(`${getLeadTracks}/users`, {
        ...body,
        leadId,
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Track assigned to member");
      if (entityId) invalidateEntityTracks(queryClient, entityType, entityId);
      invalidateLeadTimeline(leadId);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to assign member";
      toast.error(msg);
    },
  });

  const removeTrack = useMutation({
    mutationFn: async (target: number | { trackId: number; childId?: number }) => {
      const { trackId, childId } =
        typeof target === "number" ? { trackId: target, childId: undefined } : target;
      return axiosInstance.delete(`${getLeadTracks}/${leadId}/${trackId}`, {
        // A deal-based track is removed from ONE deal.
        ...(childId != null ? { params: { childId } } : {}),
      });
    },
    onSuccess: () => {
      toast.success("Track removed successfully");
      if (entityId) invalidateEntityTracks(queryClient, entityType, entityId);
      invalidateLeadTimeline(leadId);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to remove track";
      toast.error(msg);
    },
  });

  return {
    assignTrack,
    switchSubTrack,
    removeTrack,
    assignTrackUser,
    isAssigning: assignTrack.isPending,
    isSwitching: switchSubTrack.isPending,
    isRemoving: removeTrack.isPending,
  };
}
